import { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, AttributionControl, useMap } from "react-leaflet";
import { RefreshCw, Radio, Users, Gauge, Clock, Sun, Moon } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { DARK_TILES, LIGHT_TILES, TILE_ATTRIB, vehicleIcon, STATUS_COLOR } from "@/lib/mapUtils";

const ANTALYA = [36.85, 31.0];

// Imperatively fly the map to a selected vehicle.
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 13, { duration: 0.8 });
  }, [target, map]);
  return null;
}

const ago = (iso) => {
  if (!iso) return "—";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  return `${Math.round(s / 3600)}h`;
};

export default function AdminLive() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapLight, setMapLight] = useState(() => localStorage.getItem("vaya_map_light") === "1");
  const markerRefs = useRef({});

  const toggleMapTheme = () =>
    setMapLight((v) => {
      localStorage.setItem("vaya_map_light", v ? "0" : "1");
      return !v;
    });

  const isAdmin = user?.role === "admin";

  const load = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/live");
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);

  if (!isAdmin)
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <p className="mx-auto max-w-3xl px-5 py-24 text-slate-600" data-testid="live-no-access">
          {t("admin.noAccess")}
        </p>
      </div>
    );

  const vehicles = (data?.vehicles || []).filter(
    (v) => typeof v.lat === "number" && typeof v.lng === "number"
  );

  const focus = (v) => {
    setSelected({ ...v, _k: Date.now() });
    const ref = markerRefs.current[v.booking_id];
    if (ref) ref.openPopup();
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-[1600px] px-4 py-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="label-eyebrow">{t("admin.title")}</p>
            <h1 className="mt-2 flex items-center gap-2 font-display text-3xl tracking-tight sm:text-4xl" data-testid="live-title">
              <Radio size={22} className="text-sea" /> {t("live.title")}
            </h1>
            <p className="mt-2 text-sm text-slate-600">{t("live.sub")}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right text-xs text-slate-500" data-testid="live-meta">
              <p>
                <span className="font-display text-lg text-slate-900">{data?.count ?? 0}</span> {t("live.active")}
              </p>
              <p className="mt-0.5">
                {t("live.updated")}: {data?.as_of ? new Date(data.as_of).toLocaleTimeString() : "—"}
              </p>
            </div>
            <Button variant="outline" data-testid="live-map-theme-btn" onClick={toggleMapTheme} title={mapLight ? t("live.mapDark") : t("live.mapLight")}>
              {mapLight ? <Moon size={15} className="mr-1.5" /> : <Sun size={15} className="mr-1.5" />}
              {mapLight ? t("live.mapDark") : t("live.mapLight")}
            </Button>
            <Button variant="outline" data-testid="live-refresh-btn" onClick={load}>
              <RefreshCw size={15} className="mr-1.5" /> {t("common.refresh")}
            </Button>
          </div>
        </div>

        <div className="vaya-map mt-6 grid gap-4 overflow-hidden rounded-2xl border border-slate-800 lg:grid-cols-12">
          {/* Map */}
          <div className="relative h-[62vh] lg:col-span-8 lg:h-[74vh]" data-testid="live-map">
            <MapContainer
              center={ANTALYA}
              zoom={9}
              scrollWheelZoom
              attributionControl={false}
              style={{ height: "100%", width: "100%" }}
            >
              <AttributionControl prefix={false} />
              <TileLayer key={mapLight ? "light" : "dark"} url={mapLight ? LIGHT_TILES : DARK_TILES} attribution={TILE_ATTRIB} />
              <FlyTo target={selected} />
              {vehicles.map((v) => (
                <Marker
                  key={v.booking_id}
                  position={[v.lat, v.lng]}
                  icon={vehicleIcon(v.status, v.stale)}
                  ref={(r) => {
                    if (r) markerRefs.current[v.booking_id] = r;
                  }}
                >
                  <Popup>
                    <div className="min-w-[180px] text-xs">
                      <p className="font-semibold text-white">{v.driver_name || "—"}</p>
                      <p className="text-slate-300">{v.vehicle} · {v.plate}</p>
                      <p className="mt-1 text-slate-300">{v.customer_name}</p>
                      <p className="text-slate-400">{v.origin} → {v.destination}</p>
                      <p className="mt-1 font-medium" style={{ color: STATUS_COLOR[v.status] || "#94A3B8" }}>
                        {t(`status.${v.status}`) === `status.${v.status}` ? v.status : t(`status.${v.status}`)}
                        {v.eta_min ? ` · ${v.eta_min} ${t("results.min")}` : ""}
                      </p>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* Dispatch side panel */}
          <aside className="bg-slate-950 lg:col-span-4">
            <div className="border-b border-slate-800 px-5 py-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                {t("live.dispatch")} · {vehicles.length}
              </p>
            </div>
            <div className="max-h-[52vh] space-y-2 overflow-y-auto p-4 lg:max-h-[66vh]" data-testid="live-dispatch-list">
              {loading && <p className="text-sm text-slate-500">{t("common.loading")}</p>}
              {!loading && vehicles.length === 0 && (
                <p className="text-sm text-slate-500" data-testid="live-empty">{t("live.noVehicles")}</p>
              )}
              {vehicles.map((v) => (
                <button
                  key={v.booking_id}
                  data-testid={`live-vehicle-${v.code}`}
                  onClick={() => focus(v)}
                  className="w-full rounded-xl border border-slate-800 bg-slate-900/70 p-3 text-left transition-colors duration-200 hover:border-sea/60 hover:bg-slate-900"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-white">{v.driver_name || "—"}</p>
                      <p className="text-[11px] text-slate-400">{v.vehicle} · {v.plate}</p>
                    </div>
                    <StatusBadge status={v.status} testId={`live-status-${v.code}`} />
                  </div>
                  <p className="mt-2 text-[11px] text-slate-400">
                    <span className="text-slate-300">{v.code}</span> · {v.origin} → {v.destination}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] text-slate-400">
                    <span className="inline-flex items-center gap-1"><Users size={11} /> {v.customer_name}</span>
                    {typeof v.speed_kmh === "number" && v.speed_kmh >= 0 && (
                      <span className="inline-flex items-center gap-1"><Gauge size={11} /> {v.speed_kmh} km/h</span>
                    )}
                    {v.eta_min ? (
                      <span className="inline-flex items-center gap-1"><Clock size={11} /> {v.eta_min} {t("results.min")}</span>
                    ) : null}
                    <span className={v.stale ? "text-amber-400" : "text-emerald-400"}>
                      {v.stale ? t("live.stale") : t("live.livePing")} · {ago(v.recorded_at)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
